export type UserRole = "Officer" | "SCM Manager" | "CFO/HOD" | "System Admin";

export interface User {
  id: string;
  name: string;
  email: string;
  department: string;
  role: UserRole;
  status: "Active" | "Inactive";
  lastActive: string;
  avatar: string;
}

export const roleColors: Record<UserRole, { bg: string; text: string; dot: string }> = {
  "System Admin": { bg: "bg-accent-50", text: "text-accent-700", dot: "bg-accent-500" },
  "CFO/HOD": { bg: "bg-primary-50", text: "text-primary-700", dot: "bg-primary-500" },
  "SCM Manager": { bg: "bg-secondary-50", text: "text-secondary-700", dot: "bg-secondary-500" },
  "Officer": { bg: "bg-background-200", text: "text-foreground-600", dot: "bg-background-400" },
};

export const rolePrivileges: Record<UserRole, string[]> = {
  "System Admin": [
    "Full system access",
    "User & role management",
    "Alert configuration",
    "All reports & exports",
    "Audit log access",
    "Contract management",
    "Supplier reviews",
  ],
  "CFO/HOD": [
    "View all reports",
    "Approve contract extensions",
    "Alert settings configuration",
    "View department contracts",
    "Supplier reviews (view only)",
  ],
  "SCM Manager": [
    "Contract management (create/edit)",
    "Supplier reviews (all actions)",
    "View department contracts",
    "Alert acknowledgment",
    "Reports (department only)",
  ],
  "Officer": [
    "View assigned contracts",
    "Submit supplier reviews",
    "Alert acknowledgment",
    "Basic reporting",
  ],
};

export const mockDepartmentsList = [
  "Department of Home Affairs",
  "Department of Public Works",
  "National Treasury",
  "SARS",
  "Department of Transport",
  "Department of Health",
  "Department of Basic Education",
  "Department of Correctional Services",
  "Auditor-General of South Africa",
  "Department of Social Development",
  "Department of Defence",
  "Department of Water and Sanitation",
];

export const mockUsers: User[] = [
  {
    id: "usr-001",
    name: "Thabo Molefe",
    email: "thabo.molefe@dha.gov.za",
    department: "Department of Home Affairs",
    role: "Officer",
    status: "Active",
    lastActive: "2026-06-18T08:30:00Z",
    avatar: "TM",
  },
  {
    id: "usr-002",
    name: "Nomsa Dlamini",
    email: "nomsa.dlamini@dpw.gov.za",
    department: "Department of Public Works",
    role: "SCM Manager",
    status: "Active",
    lastActive: "2026-06-18T09:15:00Z",
    avatar: "ND",
  },
  {
    id: "usr-003",
    name: "Sipho Khumalo",
    email: "sipho.khumalo@treasury.gov.za",
    department: "National Treasury",
    role: "CFO/HOD",
    status: "Active",
    lastActive: "2026-06-17T16:45:00Z",
    avatar: "SK",
  },
  {
    id: "usr-004",
    name: "Lindiwe Nkosi",
    email: "lindiwe.nkosi@sars.gov.za",
    department: "SARS",
    role: "Officer",
    status: "Active",
    lastActive: "2026-06-18T07:50:00Z",
    avatar: "LN",
  },
  {
    id: "usr-005",
    name: "Mandla Zulu",
    email: "mandla.zulu@dot.gov.za",
    department: "Department of Transport",
    role: "SCM Manager",
    status: "Active",
    lastActive: "2026-06-17T14:20:00Z",
    avatar: "MZ",
  },
  {
    id: "usr-006",
    name: "Dr. Palesa Mokoena",
    email: "palesa.mokoena@health.gov.za",
    department: "Department of Health",
    role: "CFO/HOD",
    status: "Active",
    lastActive: "2026-06-18T10:05:00Z",
    avatar: "PM",
  },
  {
    id: "usr-007",
    name: "Busisiwe Mthembu",
    email: "busisiwe.mthembu@dbe.gov.za",
    department: "Department of Basic Education",
    role: "Officer",
    status: "Inactive",
    lastActive: "2026-05-22T11:30:00Z",
    avatar: "BM",
  },
  {
    id: "usr-008",
    name: "Jabulani Sibeko",
    email: "jabulani.sibeko@dcs.gov.za",
    department: "Department of Correctional Services",
    role: "SCM Manager",
    status: "Active",
    lastActive: "2026-06-18T08:00:00Z",
    avatar: "JS",
  },
  {
    id: "usr-009",
    name: "Nomvula Dube",
    email: "nomvula.dube@agsa.gov.za",
    department: "Auditor-General of South Africa",
    role: "System Admin",
    status: "Active",
    lastActive: "2026-06-18T09:55:00Z",
    avatar: "ND",
  },
  {
    id: "usr-010",
    name: "Thandiwe Mahlangu",
    email: "thandiwe.mahlangu@dsd.gov.za",
    department: "Department of Social Development",
    role: "Officer",
    status: "Active",
    lastActive: "2026-06-17T15:10:00Z",
    avatar: "TM",
  },
  {
    id: "usr-011",
    name: "Col. Hendrik van der Merwe",
    email: "hendrik.vdmerwe@dod.gov.za",
    department: "Department of Defence",
    role: "SCM Manager",
    status: "Active",
    lastActive: "2026-06-18T06:30:00Z",
    avatar: "HV",
  },
  {
    id: "usr-012",
    name: "Rethabile Mofokeng",
    email: "rethabile.mofokeng@dws.gov.za",
    department: "Department of Water and Sanitation",
    role: "Officer",
    status: "Active",
    lastActive: "2026-06-18T07:15:00Z",
    avatar: "RM",
  },
  {
    id: "usr-013",
    name: "Zanele Buthelezi",
    email: "zanele.buthelezi@dha.gov.za",
    department: "Department of Home Affairs",
    role: "System Admin",
    status: "Active",
    lastActive: "2026-06-18T10:30:00Z",
    avatar: "ZB",
  },
  {
    id: "usr-014",
    name: "David Ndlovu",
    email: "david.ndlovu@treasury.gov.za",
    department: "National Treasury",
    role: "Officer",
    status: "Inactive",
    lastActive: "2026-04-10T12:00:00Z",
    avatar: "DN",
  },
];